import type { OrganizationConfigConfig } from '@/types/organization-config'
import type { LzpUser } from '@/types/user.ts'
import { require, useToastStore } from '@logistiek-zonder-papier/lzp-library'
import '@logistiek-zonder-papier/lzp-library/style.css'
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

export type AppInitResponse = {
  user: LzpUser
  can_modify_translations: boolean
  userModules: number[]
  orgConfig: OrganizationConfigConfig
  translations: Record<string, unknown>
}

type UnknownThis = {
  optional: (element: HTMLElement) => boolean
}

async function refreshToken(): Promise<string | null> {
  let res: AxiosResponse<string>
  try {
    res = await axios.get('/auth/api/refresh')
  } catch (error: unknown) {
    if (!axios.isAxiosError(error)) {
      throw error
    }
    sessionStorage.clear()
    sessionStorage.setItem('desired-location', window.location.pathname)
    sessionStorage.setItem('desired-query', window.location.search)
    window.location.href = '/login'
    return null
  }
  window.localStorage.setItem('jwt_token', res.data)
  return res.data
}

// TODO - remove once everyone uses a central axios client
function axiosRequestInterceptor(request: InternalAxiosRequestConfig) {
  request.baseURL = import.meta.env['VITE_BASE_URL']
  const csrfToken = document.cookie.replace(/(?:^|.*;\s*)XSRF-TOKEN\s*=\s*([^;]*).*$|^.*$/, '$1')
  if (request.headers && !request.headers['Authorization']) {
    request.headers['X-XSRF-TOKEN'] = csrfToken
    request.headers.Authorization = `Bearer ${window.localStorage.getItem('jwt_token')}`
  }
  request.withCredentials = true
  return request
}

// TODO - remove once everyone uses a central axios client
// eslint-disable-next-line
async function axiosErrorResponseInterceptor(error: any) {
  const originalRequest = error.config
  if (
    (error.response?.data.status == 1 || error.response.data.status == -1) &&
    !originalRequest.ignoreError
  ) {
    require(document.getElementById('global-error-modal-message')).innerHTML =
      error.response.data.message
    window.globalErrorModal.show()
  }

  // Keep track of if this request has already been tried again, as to prevent an infinte request loop.
  if (
    error.response &&
    error.response.status == 401 &&
    originalRequest.url != '/auth/api/refresh'
  ) {
    const token = await refreshToken()
    if (token != null) {
      originalRequest.headers['Authorization'] = `Bearer ${token}`
      return axios(originalRequest)
    }
  }
  return Promise.reject(error)
}

export function loadLegacy() {
  window.globalErrorModal = new bootstrap.Modal(
    require(document.getElementById('global-error-modal'))
  )
  window.globalPromptModal = new bootstrap.Modal(
    require(document.getElementById('global-prompt-modal'))
  )
  const { success, error } = useToastStore()
  window.showToast = success
  window.showErrorToast = error
  $.ajaxPrefilter(options => {
    const baseURL = import.meta.env['VITE_BASE_URL']
    if (options.url && options.url.startsWith('/')) {
      options.url = baseURL.replace(/\/$/, '') + options.url
    }
  })

  $.ajaxSetup({
    xhrFields: { withCredentials: true },
    beforeSend: function (jqXHR) {
      const jwt = window.localStorage.getItem('jwt_token')
      if (jwt) jqXHR.setRequestHeader('Authorization', `Bearer ${jwt}`)
    },
  })

  $.validator.addMethod(
    'regex',
    function (this: UnknownThis, value, element, regexp): boolean {
      const re = new RegExp(regexp)
      return this.optional(element) || re.test(value)
    },
    'Please check your input.'
  )

  axios.interceptors.request.use(axiosRequestInterceptor)
  window.axios.interceptors.request.use(axiosRequestInterceptor)
  axios.interceptors.response.use(null, axiosErrorResponseInterceptor)
  window.axios.interceptors.response.use(null, axiosErrorResponseInterceptor)

  // ??
  const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
  popoverTriggerList.map(popoverTriggerEl => {
    return new bootstrap.Popover(popoverTriggerEl)
  })
  const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
  tooltipTriggerList.map(tooltipTriggerEl => {
    return new bootstrap.Tooltip(tooltipTriggerEl)
  })
}

window.logOut = async function () {
  await axios.get('/auth/api/logout')
  window.sessionStorage.clear()
  window.localStorage.clear()
  window.location.href = '/login'
}
